/**
 * Token storage + a tiny bridge between the axios layer and the React
 * <AuthProvider> that owns the PIN modal.
 *
 * Why the bridge? On a 401 the axios response-interceptor needs to
 * "ask" the user for a PIN and wait for them to enter it — but the
 * interceptor lives outside the React tree and can't render a modal.
 * So AuthProvider registers a handler once on mount; the interceptor
 * calls `requestPin()`, which resolves when the user unlocks and
 * rejects when they cancel.
 *
 * The token itself is persisted in localStorage (it's a session token,
 * not the PIN). If it's tampered with or expired the backend returns
 * 401 and we simply prompt again — no security regression.
 */

const TOKEN_KEY = "refclick.token";

export function getToken() {
  try {
    return window.localStorage.getItem(TOKEN_KEY) || "";
  } catch {
    return "";
  }
}

export function setToken(t) {
  try {
    if (t) window.localStorage.setItem(TOKEN_KEY, t);
    else window.localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* private browsing: swallow */
  }
}

export function clearToken() {
  setToken("");
}

// ---- Pub/sub bridge from axios → React ------------------------------------

let pinRequestHandler = null;

/** AuthProvider calls this once on mount. Returns a cleanup fn. */
export function registerPinHandler(fn) {
  pinRequestHandler = fn;
  return () => {
    if (pinRequestHandler === fn) pinRequestHandler = null;
  };
}

/**
 * Axios interceptor entry point.
 * Resolves when the user has unlocked (a fresh token is now in storage).
 * Rejects when the user cancels or the handler isn't ready.
 */
export function requestPin() {
  if (!pinRequestHandler) {
    return Promise.reject(new Error("PIN prompt is not ready yet"));
  }
  return pinRequestHandler();
}

// ---- Simple in-tab pub/sub so header/detail can react to lock state -------

const listeners = new Set();
export function subscribeAuth(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
export function emitAuthChange(unlocked) {
  for (const fn of listeners) {
    try {
      fn(unlocked);
    } catch {
      /* keep other listeners running */
    }
  }
}
